<div class="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">

  @if (isLoading()) {
    <div class="flex flex-col items-center gap-3">
      <div class="w-8 h-8 border-2 border-primary-800 border-t-transparent rounded-full animate-spin"></div>
      <p class="text-xs text-slate-500 dark:text-slate-400">Loading form...</p>
    </div>

  } @else if (isInvalid()) {
    <div class="w-full max-w-md text-center">
      <div class="w-14 h-14 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
        <svg class="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
        </svg>
      </div>
      <h2 class="text-lg font-bold text-slate-800 dark:text-white mb-2">Form Not Available</h2>
      <p class="text-sm text-slate-500 dark:text-slate-400">This application form is no longer available or the link has expired. Please contact your institution for assistance.</p>
    </div>

  } @else if (isSubmitted()) {
    <div class="w-full max-w-md text-center">
      <div class="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-950/40 flex items-center justify-center mx-auto mb-4">
        <svg class="w-7 h-7 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 class="text-lg font-bold text-slate-800 dark:text-white mb-2">Application Submitted!</h2>
      <p class="text-sm text-slate-500 dark:text-slate-400">Your application has been submitted successfully. We will be in touch with you shortly.</p>
    </div>

  } @else {
    <div class="w-full max-w-lg">
      <!-- Card -->
      <div class="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
        <!-- Header -->
        <div class="px-7 py-5 border-b border-slate-100 dark:border-slate-700/50 bg-slate-50 dark:bg-slate-800/60">
          <h2 class="text-base font-bold text-slate-900 dark:text-white">Loan Application</h2>
          @if (formConfig()?.institutionName) {
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{{ formConfig()?.institutionName }} · {{ formConfig()?.productName }}</p>
          }
        </div>

        <!-- Form -->
        <form [formGroup]="dynamicForm" (ngSubmit)="submit()" class="px-7 py-6 space-y-4">
          @for (field of fields(); track field.key) {
            <div>
              <label [for]="field.key"
                class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">
                {{ field.label }}
                @if (field.required) {
                  <span class="text-red-500 ml-0.5">*</span>
                }
              </label>

              @switch (field.type) {
                @case ('SELECT') {
                  <select [id]="field.key" [formControlName]="field.key"
                    class="w-full h-10 px-3 text-xs bg-white dark:bg-slate-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800/20 text-slate-800 dark:text-slate-100 transition-colors"
                    [class]="isControlInvalid(field.key) ? 'border-red-400 dark:border-red-500' : 'border-slate-300 dark:border-slate-600'">
                    <option value="">{{ field.placeholder || 'Select...' }}</option>
                    @for (option of field.options; track option.value) {
                      <option [value]="option.value">{{ option.label }}</option>
                    }
                  </select>
                }
                @case ('MULTI_SELECT') {
                  <select [id]="field.key" [formControlName]="field.key" multiple
                    class="w-full px-3 py-2 text-xs bg-white dark:bg-slate-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800/20 text-slate-800 dark:text-slate-100 transition-colors"
                    [class]="isControlInvalid(field.key) ? 'border-red-400 dark:border-red-500' : 'border-slate-300 dark:border-slate-600'">
                    @for (option of field.options; track option.value) {
                      <option [value]="option.value">{{ option.label }}</option>
                    }
                  </select>
                }
                @case ('DATE') {
                  <input [id]="field.key" [formControlName]="field.key" type="date"
                    class="w-full h-10 px-3 text-xs bg-white dark:bg-slate-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800/20 text-slate-800 dark:text-slate-100 transition-colors"
                    [class]="isControlInvalid(field.key) ? 'border-red-400 dark:border-red-500' : 'border-slate-300 dark:border-slate-600'">
                }
                @case ('FILE') {
                  <input [id]="field.key" type="file"
                    (change)="onFileChange($event, field.key)"
                    class="w-full text-xs text-slate-600 dark:text-slate-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-slate-100 dark:file:bg-slate-700 file:text-slate-700 dark:file:text-slate-200 hover:file:bg-slate-200 dark:hover:file:bg-slate-600 cursor-pointer">
                }
                @case ('BOOLEAN') {
                  <div class="flex items-center gap-2">
                    <input [id]="field.key" [formControlName]="field.key" type="checkbox"
                      class="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-primary-800 focus:ring-primary-800/20">
                    <label [for]="field.key" class="text-xs text-slate-600 dark:text-slate-300">Yes</label>
                  </div>
                }
                @default {
                  <input [id]="field.key" [formControlName]="field.key" [type]="field.type | lowercase"
                    [placeholder]="field.placeholder || ''"
                    class="w-full h-10 px-3 text-xs bg-white dark:bg-slate-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800/20 text-slate-800 dark:text-slate-100 placeholder:text-slate-400 transition-colors"
                    [class]="isControlInvalid(field.key) ? 'border-red-400 dark:border-red-500' : 'border-slate-300 dark:border-slate-600'">
                }
              }

              @if (isControlInvalid(field.key)) {
                <p class="mt-1 text-[10px] text-red-500 dark:text-red-400">{{ field.label }} is required.</p>
              }
            </div>
          }

          @if (submitError()) {
            <div class="p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900">
              <p class="text-xs text-red-600 dark:text-red-400">{{ submitError() }}</p>
            </div>
          }

          <button type="submit" [disabled]="isSubmitting()"
            class="w-full py-2.5 rounded-lg text-sm font-bold bg-primary-800 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mt-2">
            {{ isSubmitting() ? 'Submitting...' : 'Submit Application' }}
          </button>
        </form>
      </div>
    </div>
  }
</div>
